/*
 * Copyright (c) CIRROM 2025.
 */

import { API } from "./api";
export interface UserActivitySummary {
  user_id: number;
  user_name: string;
  disabled: boolean;
  total_changes: number;
  approved_changes: number;
  rejected_changes: number;
  duplicates_processed: number;
}
export const getUserActivitySummary = async (
  integratedSystemId?: number,
  dataProjectId?: number
): Promise<UserActivitySummary[]> => {
  const response = await API.get("/get-user-activity-summary", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export interface UserActivityByField {
  user_id: number;
  user_name: string;
  field: string;
  change_count: number;
  approved_count: number;
  rejected_count: number;
  approval_rate: number;
  rejection_rate: number;
}

export const getUserActivityByField = async (
  integratedSystemId?: number,
  dataProjectId?: number
): Promise<UserActivityByField[]> => {
  const response = await API.get("/get-user-activity-by-field", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export interface UserMonthlyActivity {
  activity_month: Date;
  user_id: number;
  user_name: string;
  monthly_changes: number;
  monthly_approved: number;
  monthly_rejected: number;
  monthly_approval_rate: number;
  monthly_rejection_rate: number;
}

export const getUserActivityMonthly = async (
  integratedSystemId?: number,
  dataProjectId?: number
) => {
  const response = await API.get("/get-user-activity-monthly", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export interface FieldDelta {
  CONTACT: number;
  ACCOUNT: number;
}

export interface StatForSystemOrProject {
  CONTACT: number;
  ACCOUNT: number;
  total: number;
  delta: FieldDelta;
}

export interface StatsForSystemOrProject {
  name_fields: {
    first_name: StatForSystemOrProject;
    last_name: StatForSystemOrProject;
    suffix: StatForSystemOrProject;
  };
  email_fields: {
    primary_email: StatForSystemOrProject;
  };
  location_fields: {
    city: StatForSystemOrProject;
    country: StatForSystemOrProject;
    stateorprovince: StatForSystemOrProject;
    zipcode: StatForSystemOrProject;
    telephone: StatForSystemOrProject;
    address_linkage: StatForSystemOrProject;
  };
  employment_fields: {
    job_title: StatForSystemOrProject;
    company: StatForSystemOrProject;
  };
  duplicates: { total: number; delta: number };
  account_duplicates: { total: number; delta: number };
  address_duplicates: { total: number; delta: number };
  batch_id: number;
}

export interface SummaryByFieldOverBatch {
  batch_id: number;
  last_update_time: string;
  change_field: string;
  total: number;
}

export const getSummaryByFieldOverBatches = async (
  integratedSystemId?: number,
  dataProjectId?: number
): Promise<SummaryByFieldOverBatch[]> => {
  const response = await API.get("/get-summary-by-field-over-batches", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export interface ContactProblemSummary {
  contact_id: string;
  contact_created_date: string;
  problem_count: number;
  problem_count_80: number;
  is_duplicate: boolean;
}

export const getContactProblemSummary = async (
  integratedSystemId?: number,
  dataProjectId?: number
): Promise<ContactProblemSummary[]> => {
  const response = await API.get("/get-contact-problem-summary", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};

export const getStatsForSystemOrProject = async (
  integratedSystemId?: number,
  dataProjectId?: number
): Promise<StatsForSystemOrProject> => {
  const response = await API.get("/get-stats-for-system-or-project", {
    params: {
      integrated_system_id: integratedSystemId,
      data_project_id: dataProjectId,
    },
  });
  return response.data;
};
